import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getAuthErrorMessageFromError } from '../lib/authErrors'
import AuthError from '../components/AuthError'
import type { AuthType } from '../types/auth'
import { webRedirectTarget } from '../lib/authRedirect'

const VALID_TYPES: AuthType[] = ['signup', 'recovery', 'email_change', 'magiclink']

function AuthConfirm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleConfirm = async () => {
      const tokenHash = searchParams.get('token_hash')
      const type = searchParams.get('type') as AuthType | null

      if (!tokenHash || !type) {
        setError('Missing required parameters. Please use the link from your email.')
        return
      }

      if (!VALID_TYPES.includes(type)) {
        setError('Invalid confirmation type.')
        return
      }

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type, // AuthType values match EmailOtpType
      })

      if (verifyError) {
        setError(getAuthErrorMessageFromError(verifyError))
        return
      }

      // ON-407: a web signup asked to come back to where it started.
      const target = webRedirectTarget(type, searchParams.get('redirect_to'), data?.user)
      if (target) {
        navigate(target, { replace: true })
        return
      }

      // Route based on confirmation type
      switch (type) {
        case 'recovery':
          navigate('/reset-password', { replace: true })
          break
        case 'signup':
        case 'email_change':
        case 'magiclink':
          navigate('/confirm-email', {
            replace: true,
            state: { type },
          })
          break
        default:
          navigate('/', { replace: true })
      }
    }

    handleConfirm()
  }, [navigate, searchParams])

  if (error) {
    return (
      <AuthError
        title="Confirmation Failed"
        message={error}
        details="The link may have expired or already been used. Please request a new link and try again."
      />
    )
  }

  return (
    <div className="card">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Confirming your request...</p>
      </div>
    </div>
  )
}

export default AuthConfirm
